import { useRef, useState, useCallback, type DragEvent } from 'react';
import clsx from 'clsx';
import { Panel, Button } from '@/components/ui';
import { useIngestModel, useModels } from '@/hooks/use-models-v2';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';

interface ModelUploadProps {
  onIngested?: (modelId: string) => void;
}

type Mode = 'new' | 'update';

export function ModelUpload({ onIngested }: ModelUploadProps = {}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>('new');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [modelName, setModelName] = useState('');
  const [modelVersion, setModelVersion] = useState('1.0.0');
  const [description, setDescription] = useState('');
  const [force, setForce] = useState(false);
  const { mutate, isPending, isSuccess, isError, error, data, reset } = useIngestModel();
  const { data: modelsData } = useModels();
  const existingModels = (modelsData as { data?: { id: string; name: string; version: string }[] })?.data ?? [];

  // Auto-bump version when selecting a model to update
  const handleSelectModel = useCallback((id: string) => {
    setSelectedModelId(id);
    const existing = existingModels.find(m => m.id === id);
    if (existing?.version) {
      const parts = existing.version.split('.');
      const patch = parseInt(parts[2] ?? '0', 10) + 1;
      setModelVersion(`${parts[0]}.${parts[1]}.${patch}`);
    }
  }, [existingModels]);

  const handleFile = useCallback((file: File) => {
    if (file.name.endsWith('.xlsx')) {
      setSelectedFile(file);
      if (!modelName && mode === 'new') {
        setModelName(file.name.replace('.xlsx', '').replace(/[-_]/g, ' '));
      }
      reset();
    }
  }, [reset, modelName, mode]);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleUpload = useCallback(() => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);

    if (mode === 'update' && selectedModelId) {
      // Update existing — send the existing model's ID
      formData.append('id', selectedModelId);
      const existing = existingModels.find(m => m.id === selectedModelId);
      formData.append('name', existing?.name ?? selectedModelId);
    } else {
      // Create new — derive ID from entered name
      if (modelName) formData.append('name', modelName);
    }

    if (modelVersion) formData.append('version', modelVersion);
    if (description) formData.append('description', description);

    mutate({ formData, force: mode === 'update' && force }, {
      onSuccess: (res) => {
        if (onIngested && res?.data?.modelId) onIngested(res.data.modelId);
      },
    });
  }, [selectedFile, mode, selectedModelId, modelName, modelVersion, description, force, mutate, onIngested, existingModels]);

  return (
    <Panel title="Upload Model">
      <div className="flex flex-col gap-3">
        {/* Mode toggle */}
        <div className="flex gap-1">
          {(['new', 'update'] as const).map(m => (
            <button
              key={m}
              onClick={() => { setMode(m); reset(); }}
              className={clsx(
                'px-3 py-1.5 text-xs font-mono font-semibold rounded-[2px] cursor-pointer border transition-colors',
                mode === m
                  ? 'bg-accent-dim border-accent/20 text-accent'
                  : 'bg-transparent border-panel-border text-text-dim hover:text-text-muted',
              )}
            >
              {m === 'new' ? 'New Model' : 'Update Existing'}
            </button>
          ))}
        </div>

        {/* Model selector for update mode */}
        {mode === 'update' && (
          <div className="flex items-center gap-2.5">
            <label className="text-xs text-text-muted font-mono w-24 shrink-0">Model</label>
            <select
              value={selectedModelId}
              onChange={e => handleSelectModel(e.target.value)}
              className="flex-1 py-2 px-3 bg-input-bg border border-input-border rounded-[3px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
            >
              <option value="">Select a model to update...</option>
              {existingModels.map(m => (
                <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
              ))}
            </select>
          </div>
        )}

        {/* Drop zone */}
        <div
          className={clsx(
            'flex flex-col items-center justify-center py-8 px-4 border border-dashed rounded-[3px] cursor-pointer transition-all duration-200',
            dragOver ? 'border-accent bg-accent/[0.04]' : 'border-panel-border hover:border-accent/30',
          )}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="drop-zone"
        >
          <Upload size={24} className="text-text-dim mb-2" />
          <span className="text-sm font-mono text-text-muted mb-1">
            {selectedFile ? selectedFile.name : 'Drop .xlsx file here or click to browse'}
          </span>
          <span className="text-xs font-mono text-text-dim">Excel workbooks only (.xlsx)</span>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
          data-testid="file-input"
        />

        {/* Metadata fields — shown after file is selected */}
        {selectedFile && (
          <div className="flex flex-col gap-2.5 py-1 overflow-hidden">
            {mode === 'new' && (
              <div className="flex items-center gap-2.5">
                <label className="text-xs text-text-muted font-mono w-24 shrink-0">Name</label>
                <input
                  value={modelName}
                  onChange={e => setModelName(e.target.value)}
                  placeholder="My Model"
                  className="flex-1 py-2 px-3 bg-input-bg border border-input-border rounded-[3px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
                />
              </div>
            )}
            <div className="flex items-center gap-2.5">
              <label className="text-xs text-text-muted font-mono w-24 shrink-0">Version</label>
              <input
                value={modelVersion}
                onChange={e => setModelVersion(e.target.value)}
                placeholder="1.0.0"
                className="flex-1 py-2 px-3 bg-input-bg border border-input-border rounded-[3px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
              />
            </div>
            <div className="flex items-start gap-2.5">
              <label className="text-xs text-text-muted font-mono w-20 shrink-0 mt-2">Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Multifamily underwriting model for..."
                rows={2}
                className="flex-1 py-2 px-3 bg-input-bg border border-input-border rounded-[3px] text-text-bright text-sm font-body outline-none focus:border-accent/20 resize-none"
              />
            </div>
            {mode === 'update' && (
              <label
                htmlFor="force-ingest"
                className="flex items-start gap-2.5 cursor-pointer pl-[6.25rem]"
                data-testid="force-toggle"
              >
                <input
                  id="force-ingest"
                  type="checkbox"
                  checked={force}
                  onChange={e => setForce(e.target.checked)}
                  className="mt-0.5 accent-warn shrink-0 cursor-pointer"
                />
                <span className="text-xs font-mono text-text-muted leading-snug">
                  Force re-ingest <span className="text-warn">(override breaking-change check)</span>
                  <br />
                  <span className="text-text-dim">Bypasses the additive-only contract. Use only when the new workbook intentionally removes or renames inputs.</span>
                </span>
              </label>
            )}
          </div>
        )}

        {/* Upload button */}
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!selectedFile || isPending || (mode === 'update' && !selectedModelId)}
          data-testid="upload-button"
        >
          {isPending ? 'Ingesting model...' : mode === 'update' ? 'Update & Re-ingest' : 'Upload & Ingest Model'}
        </Button>

        {/* Status messages */}
        {isSuccess && data && (
          <div className="flex items-center gap-2 px-3 py-2 bg-accent/[0.06] border border-accent/13 rounded-[3px]" data-testid="upload-success">
            <CheckCircle size={14} className="text-accent shrink-0" />
            <span className="text-sm font-mono text-accent">
              Model {mode === 'update' ? 'updated' : 'ingested'} successfully
            </span>
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-danger/[0.06] border border-danger/13 rounded-[3px]" data-testid="upload-error">
            <AlertCircle size={14} className="text-danger shrink-0" />
            <span className="text-sm font-mono text-danger">
              {error instanceof Error ? error.message : 'Upload failed'}
            </span>
          </div>
        )}
      </div>
    </Panel>
  );
}
